import { ApolloLink, type NormalizedCacheObject } from '@apollo/client';
import { NextSSRApolloClient } from '@apollo/experimental-nextjs-app-support/ssr';
import {
  isSyncing,
  isRefreshing,
} from '@app/(compass)/home/dashboard/settings/accounts/components/financial-institutions';
import { financialPortalsQuery } from '@components/financial-institutions/plaid-sandbox/queries';
import { FinancialPortal } from '@generated-gql/generated-types';

const FINANCIAL_PORTALS_QUERY_OPERATION_NAME =
  // @ts-expect-error "name" is not typed
  financialPortalsQuery.definitions[0]?.name?.value;

const DELAY = 5000; // 5 seconds
const MAX_RETRIES = 5;

let timeout = null;
let retries = 0;

/**
 * This is for refetching a financial account if it is syncing or refreshing.
 * This state happens right after the user has linked a financial account.
 */
const financialPortalAccountSync = new ApolloLink((operation, forward) => {
  return forward(operation).map((data) => {
    const { operationName } = operation;

    if (
      !FINANCIAL_PORTALS_QUERY_OPERATION_NAME ||
      FINANCIAL_PORTALS_QUERY_OPERATION_NAME !== operationName
    ) {
      return data;
    }

    const financialPortals = data?.data?.linkedFinancialPortals
      ?.portals as FinancialPortal[];

    if (financialPortals?.length > 0) {
      const isSyncingOrRefreshing = financialPortals.some(
        (portal) => isSyncing(portal) || isRefreshing(portal),
      );

      const hasErrors = data?.errors?.length > 0;

      if (isSyncingOrRefreshing && retries < MAX_RETRIES) {
        if (timeout) {
          clearTimeout(timeout);
        }

        timeout = setTimeout(() => {
          const apolloClient = operation.getContext()
            .client as NextSSRApolloClient<NormalizedCacheObject>;

          retries += 1;

          apolloClient?.cache?.evict({
            fieldName: 'linkedFinancialPortals',
          });
        }, DELAY);
      } else if (!isSyncingOrRefreshing && !hasErrors) {
        retries = 0;
      }
    }

    return data;
  });
});

export default financialPortalAccountSync;
